/** Shared helpers for trip logistics (stays / transports) and timelines. */

export const CURRENCY_OPTIONS = ['BRL', 'USD', 'EUR', 'SGD', 'MYR', 'CZK', 'THB', 'IDR'];
export const STATUS_OPTIONS = ['previsto', 'confirmado', 'cancelado'];

let _tempIdCounter = 0;
export const nextTempId = () => `temp-${Date.now()}-${_tempIdCounter++}`;

export const emptyTransport = () => ({
  _tempId: nextTempId(),
  ref_code: '',
  status: '',
  order: null,
  responsible: '',
  mode: '',
  description: '',
  carrier: '',
  booking_ref: '',
  cost: '',
  currency: 'BRL',
  origin: '',
  destination: '',
  departure_date: '',
  departure_time: '',
  arrival_date: '',
  arrival_time: '',
  notes: '',
});

export const emptyStay = () => ({
  _tempId: nextTempId(),
  ref_code: '',
  status: '',
  order: null,
  name: '',
  address: '',
  checkin_date: '',
  checkin_time: '',
  checkout_date: '',
  checkout_time: '',
  platform: '',
  cost: '',
  currency: 'BRL',
  notes: '',
});

export function mapTransportFromApi(t) {
  return {
    _tempId: nextTempId(),
    id: t.id,
    ref_code: t.ref_code || '',
    status: t.status || '',
    order: t.order ?? null,
    responsible: t.responsible || '',
    mode: t.mode || '',
    description: t.description || '',
    carrier: t.carrier || '',
    booking_ref: t.booking_ref || '',
    cost: t.cost ?? '',
    currency: t.currency || 'BRL',
    origin: t.origin || '',
    destination: t.destination || '',
    departure_date: t.departure_date || '',
    departure_time: t.departure_time || '',
    arrival_date: t.arrival_date || '',
    arrival_time: t.arrival_time || '',
    notes: t.notes || '',
  };
}

export function mapStayFromApi(s) {
  return {
    _tempId: nextTempId(),
    id: s.id,
    ref_code: s.ref_code || '',
    status: s.status || '',
    order: s.order ?? null,
    name: s.name || '',
    address: s.address || '',
    checkin_date: s.checkin_date || '',
    checkin_time: s.checkin_time || '',
    checkout_date: s.checkout_date || '',
    checkout_time: s.checkout_time || '',
    platform: s.platform || '',
    cost: s.cost ?? '',
    currency: s.currency || 'BRL',
    notes: s.notes || '',
  };
}

/** Map nested trip.transports / trip.stays into local form shape (with _tempId). */
export function mapTripLogistics(trip) {
  if (!trip) {
    return { transports: [], stays: [] };
  }
  return {
    transports: (trip.transports || []).map(mapTransportFromApi),
    stays: (trip.stays || []).map(mapStayFromApi),
  };
}

/** Apply logistics mapping onto a trip object (ensures transports/stays arrays). */
export function withMappedTripLogistics(trip) {
  if (!trip) return null;
  return { ...trip, ...mapTripLogistics(trip) };
}

function transportPayload(t) {
  return {
    ref_code: t.ref_code,
    status: t.status,
    order: t.order === '' || t.order === undefined ? null : t.order,
    responsible: t.responsible,
    mode: t.mode,
    description: t.description,
    carrier: t.carrier,
    booking_ref: t.booking_ref,
    cost: t.cost === '' || t.cost === null || t.cost === undefined ? null : t.cost,
    currency: t.currency,
    origin: t.origin,
    destination: t.destination,
    departure_date: t.departure_date || null,
    departure_time: t.departure_time || '',
    arrival_date: t.arrival_date || null,
    arrival_time: t.arrival_time || '',
    notes: t.notes,
  };
}

function stayPayload(s) {
  return {
    ref_code: s.ref_code,
    status: s.status,
    order: s.order === '' || s.order === undefined ? null : s.order,
    name: s.name,
    address: s.address,
    checkin_date: s.checkin_date || null,
    checkin_time: s.checkin_time || '',
    checkout_date: s.checkout_date || null,
    checkout_time: s.checkout_time || '',
    platform: s.platform,
    cost: s.cost === '' || s.cost === null || s.cost === undefined ? null : s.cost,
    currency: s.currency,
    notes: s.notes,
  };
}

/** Build replace-all transports/stays payload for TripMade update (strips _tempId). */
export function buildTripLogisticsPayload({ transports = [], stays = [] } = {}) {
  return {
    transports: transports.map(transportPayload),
    stays: stays.map(stayPayload),
  };
}

/** Parse YYYY-MM-DD + HH:MM into sortable number; null if no date. */
function toSortKey(dateStr, timeStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  let hh = 0;
  let mm = 0;
  if (timeStr && typeof timeStr === 'string' && timeStr.includes(':')) {
    const parts = timeStr.split(':').map(Number);
    if (!Number.isNaN(parts[0])) hh = parts[0];
    if (!Number.isNaN(parts[1])) mm = parts[1];
  }
  return y * 1e8 + m * 1e6 + d * 1e4 + hh * 100 + mm;
}

function transportAnchor(t) {
  const primary = toSortKey(t.departure_date, t.departure_time);
  if (primary !== null) return primary;
  return toSortKey(t.arrival_date, t.arrival_time);
}

function stayAnchor(s) {
  const primary = toSortKey(s.checkin_date, s.checkin_time);
  if (primary !== null) return primary;
  return toSortKey(s.checkout_date, s.checkout_time);
}

/**
 * Merge transports + stays into a vertical timeline.
 * Scheduled first (by date/time); transport before stay on tie; order as tertiary.
 * Items without dates go to `unscheduled`.
 */
export function buildTimelineEvents(transports = [], stays = []) {
  const events = [];

  for (const t of transports) {
    events.push({
      kind: 'transport',
      key: t._tempId || `t-${t.id}`,
      sortKey: transportAnchor(t),
      order: t.order ?? 9999,
      date: t.departure_date || t.arrival_date || '',
      time: t.departure_time || t.arrival_time || '',
      title: t.carrier || t.description || t.mode || 'Transport',
      subtitle: [t.origin, t.destination].filter(Boolean).join(' → ') || t.description || '',
      status: t.status,
      item: t,
    });
  }

  for (const s of stays) {
    events.push({
      kind: 'stay',
      key: s._tempId || `s-${s.id}`,
      sortKey: stayAnchor(s),
      order: s.order ?? 9999,
      date: s.checkin_date || s.checkout_date || '',
      time: s.checkin_time || '',
      title: s.name || 'Stay',
      subtitle: s.address || s.platform || '',
      status: s.status,
      item: s,
    });
  }

  const scheduled = events.filter((e) => e.sortKey !== null);
  const unscheduled = events.filter((e) => e.sortKey === null);

  const cmp = (a, b) => {
    if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
    // Same instant: transport before stay
    if (a.kind !== b.kind) return a.kind === 'transport' ? -1 : 1;
    return (a.order ?? 9999) - (b.order ?? 9999);
  };

  scheduled.sort(cmp);
  unscheduled.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'transport' ? -1 : 1;
    return (a.order ?? 9999) - (b.order ?? 9999);
  });

  return { scheduled, unscheduled };
}

/** Inclusive day walk YYYY-MM-DD → YYYY-MM-DD (local calendar, no TZ shift). */
function addOneDayIso(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + 1);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function eachIsoDay(startIso, endIso) {
  if (!startIso || !endIso || startIso > endIso) return [];
  const days = [];
  let cur = startIso;
  while (cur <= endIso) {
    days.push(cur);
    cur = addOneDayIso(cur);
  }
  return days;
}

function dateInInclusiveRange(day, start, end) {
  if (!start) return false;
  if (day < start) return false;
  if (end && day > end) return false;
  if (!end) return day === start;
  return true;
}

/**
 * Day-grain timeline for the Timeline tab.
 * Axis = TripMade.start_date → end_date (required). Places from trip.places;
 * stays/legs from trip.transports / trip.stays overlapping each day.
 */
export function buildDayTimeline(trip, transports = [], stays = []) {
  if (!trip?.start_date || !trip?.end_date) {
    return { days: [], error: 'missing_trip_dates' };
  }

  const places = trip.places || [];
  const days = eachIsoDay(trip.start_date, trip.end_date).map((date) => {
    const dayPlaces = places.filter((p) =>
      dateInInclusiveRange(date, p.arrival_date, p.departure_date)
    );

    const dayStays = stays
      .filter((s) => dateInInclusiveRange(date, s.checkin_date, s.checkout_date))
      .map((s) => ({
        item: s,
        isCheckin: s.checkin_date === date,
        isCheckout: s.checkout_date === date,
      }));

    const dayLegs = [];
    for (const t of transports) {
      const isDeparture = t.departure_date === date;
      const isArrival = t.arrival_date === date;
      if (isDeparture || isArrival) {
        dayLegs.push({ item: t, isDeparture, isArrival });
      }
    }

    // places[] = all covering this day; UI picks first/last/all/none via placeMode
    const primaryPlace = dayPlaces.length
      ? [...dayPlaces].sort((a, b) =>
          String(a.arrival_date || '').localeCompare(String(b.arrival_date || ''))
        )[0]
      : null;

    return {
      date,
      places: dayPlaces,
      primaryPlace,
      country: primaryPlace?.country || '',
      local: primaryPlace?.name || '',
      stays: dayStays,
      legs: dayLegs,
    };
  });

  return { days, error: null };
}

